import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "../../components/SocketContext";
import {
  getChatList,
  setSelectedChat,
  getChatMessages,
  deleteGroup,
  updateSelectedChatGroup,
  offlineUser,
  unSetSelectedChat
} from "@/store/slices/chatSlice";
import ChatList from "./chat-list";
import { ChatArea } from "./chat-message";
import CreateGroupFlow from "./create-group";
import GroupInfoModal from "./group-info-modal";
import CreateSingleChat from "./create-single-chat";
import UserInfoModal from "./user-info-modal";
import { Typography } from "@material-tailwind/react";
import Swal from "sweetalert2";
import Toaster from "@/components/Toaster";
import { useTranslation } from "react-i18next";

export function ChatPage() {
  const socket = useSocket();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.user);
  const { t } = useTranslation('chat');
  const { selectedChat,group_details } = useSelector((state) => state.chat);

  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [singleChatCreate, setSingleChatCreate] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);


  useEffect(() => {
    // This code runs when the component loads
    console.log("User entered chat");

    return () => {
      // THIS IS THE "LEAVE" EVENT
      // This runs when the user navigates to another page in your app
      dispatch(offlineUser());
      dispatch(unSetSelectedChat());
      console.log("User leave chat");
    };
  }, []);

  const handleGroupCreated = (group) => {
    setShowCreateGroup(false);
    dispatch(getChatList({ page: 1 }));

    if (group?.conversation_id) {
      dispatch(setSelectedChat(group));
      dispatch(
        getChatMessages({
          id: group.conversation_id,
          page: 1,
        })
      );
      socket.emit("join_group", group.chat_with?.id);
    }
  };

  const headerName =
    selectedChat?.chat_with?.name ||
    `${user?.first_name} ${user?.last_name || ""}`;

  const headerImage = selectedChat?.chat_with?.image || "/img/icons/user-profile.png";

  const headerInitials = headerName
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleSingleChatCreated = () => {
    setSingleChatCreate(false);
  }
    
const handleDeleteGroup = () => {
  if (!selectedChat?.chat_with?.id) return; // safeguard

  Swal.fire({
    title: t('deleteGroupTitle'),
    text: t('deleteGroupText'),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: t('deleteConfirm'),
    cancelButtonText: t('cancel'),
    reverseButtons: true,
  }).then((result) => {
    if (!result.isConfirmed) return;

    dispatch(deleteGroup(selectedChat.chat_with.id))
      .then(() => {
        // 🔔 SUCCESS ALERT
        Swal.fire({
          title: t('deletedTitle'),
          text: t('deletedText'),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });

        dispatch(updateSelectedChatGroup(null));

        setShowGroupInfo(false);
        setShowCreateGroup(false);
        setSingleChatCreate(false);
        setShowUserInfo(false);

        dispatch(
          getChatList({
            page: 1,
            search: "",
            filter: "all",
          })
        );      
        window.location.reload();
      })
      .catch((error) => {
        console.error("Delete Error:", error);
        Swal.fire({
          title: t('deleteErrorTitle'),
          text: t('deleteErrorText'),
          icon: "error",
        });
      });
  });
};


  return (
    <div className="flex flex-col w-full overflow-hidden -m-4" style={{ height: 'calc(100vh - 2rem)', maxHeight: 'calc(100vh - 2rem)' }}>
      {/* Page Header */}
      <div className="px-4 py-2  border-b border-gray-200 shrink-0">
        <Typography variant="h5" color="blue-gray" className="font-semibold text-lg">
          {t('title')}
        </Typography>
      </div>

      {/* Main Chat Container */}
      <div className="flex-1 flex bg-white overflow-hidden min-h-0">
        {/* LEFT SIDEBAR - Chat List */}
        <div className="w-[280px] flex-shrink-0 flex flex-col bg-white border-r border-gray-200">
          {/* Sidebar Header */}
          <div
            className={`h-[60px] bg-[#065C36] flex items-center px-3 text-white shrink-0 transition-all ${
              selectedChat ? 'cursor-pointer hover:bg-[#054a2b]' : 'cursor-default'
            }`}
              onClick={() => {
                if (selectedChat) {
                  setShowCreateGroup(false);
                  setSingleChatCreate(false);
                  if (selectedChat?.type === 'group') {
                    setShowGroupInfo(true);
                    setShowUserInfo(false);
                  } else {
                    setShowUserInfo(true);
                    setShowGroupInfo(false);
                  }
                }
              }}
          >
            {selectedChat ? (
              <>
                {headerImage && headerImage !== "/img/icons/user-profile.png" ? (
                  <div className="w-9 h-9 mr-2.5 rounded-full bg-white/20 flex items-center justify-center shrink-0 ring-2 ring-white/30">
                    <img src={headerImage} className="w-full h-full rounded-full object-cover" alt={headerName} />
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-full mr-2.5 bg-white/20 flex items-center justify-center font-bold shrink-0 ring-2 ring-white/30">
                    <span className="text-xs">{headerInitials}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <span className="font-semibold truncate text-xs block">{headerName}</span>
                  <span className="text-[10px] text-white/80 font-normal block truncate">
                    {selectedChat?.type === 'group' ? t('group') : t('directMessage')}
                  </span>
                </div>
              </>
            ) : (
              <span className="font-semibold text-xs">{t('selectConversation')}</span>
            )}
          </div>

          <div className="flex-1 overflow-hidden">
            <ChatList
              onCreateGroup={() => { setShowCreateGroup(true); setShowGroupInfo(false); setShowUserInfo(false); setSingleChatCreate(false); }}
              onSingleChatCreate={() => { setSingleChatCreate(true); setShowCreateGroup(false); setShowGroupInfo(false); setShowUserInfo(false); }}
            />
          </div>
        </div>

        {/* RIGHT SIDE - Chat Area + Side Panel */}
        <div className="flex-1 flex relative overflow-hidden bg-gray-50 min-w-0">
          {/* Chat Messages Area */}
          <div className="flex-1 min-w-0 h-full flex flex-col">
            {/* <ChatArea /> */}
            <ChatArea onOpenInfo={() => {
                setShowGroupInfo(true);
                setShowUserInfo(false);
                setShowCreateGroup(false);
                setSingleChatCreate(false);
            }} />
          </div>

          {/* Side Panel - Slides in from right */}
          {(showCreateGroup || singleChatCreate || showGroupInfo || showUserInfo) && (
            <div className="w-[340px] flex-shrink-0 border-l border-gray-200 bg-white h-full shadow-2xl flex flex-col relative z-10">
              {/* Panel Header */}
              <div className="h-[60px] border-b border-gray-200 flex items-center justify-between px-4 bg-white shrink-0 relative">
                <h3 className="font-semibold text-gray-900 text-base">
                  {showCreateGroup && t('newGroup')}
                  {singleChatCreate && t('newChat')}
                  {showGroupInfo && t('groupInfo')}
                  {showUserInfo && t('contactInfo')}
                </h3>

                <div className="flex items-center gap-2">
                  {showGroupInfo && group_details?.created_by === user?.id && (
                    <button
                      onClick={handleDeleteGroup}
                      className="p-2 hover:bg-red-50 rounded-full transition-colors"
                      title="Delete Group"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0H7m3-3h4a1 1 0 011 1v1H9V5a1 1 0 011-1z"
                        />
                      </svg>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setShowCreateGroup(false);
                      setSingleChatCreate(false);
                      setShowGroupInfo(false);
                      setShowUserInfo(false);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="Close panel"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Panel Content */}
              <div className="flex-1 overflow-y-auto">
                {showCreateGroup && (
                  <CreateGroupFlow 
                    onCancel={() => setShowCreateGroup(false)} 
                    onSuccess={handleGroupCreated} 
                  />
                )}
                {singleChatCreate && (
                  <CreateSingleChat 
                    onCancel={() => setSingleChatCreate(false)} 
                    onSuccess={handleSingleChatCreated} 
                  />
                )}
                {showGroupInfo && selectedChat && (
                  <GroupInfoModal 
                    chat={selectedChat} 
                    onClose={() => setShowGroupInfo(false)} 
                  />
                )}
                {showUserInfo && selectedChat && (
                  <UserInfoModal 
                    user={selectedChat} 
                    onClose={() => setShowUserInfo(false)} 
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
